import { useQuery } from "@apollo/client";
import { Button, Dialog, DialogActions, DialogContent } from "@mui/material";
import { useSnackbar } from "notistack";
import { ReactElement, useCallback, useMemo, useState } from "react";

import {
  ListComplianceBundlesQuery,
  ReuseComplianceBundleMutation,
  ReuseComplianceBundleMutationVariables,
} from "~frontend/__generated__/graphql";
import { SearchableSelect } from "~frontend/components/forms/FormSelect";
import { LIST_COMPLIANCE_BUNDLES } from "~frontend/graphql/complianceBundles";
import {
  GET_REGISTRATION_FOR_STAFF,
  REUSE_COMPLIANCE_BUNDLE,
} from "~frontend/graphql/tenDlcRegistrations";
import useMutation from "~frontend/util/useMutation";

const pageSize = 100;

export const ReuseBundleDialog = ({
  campaignRegistrationId,
  open,
  onClose,
}: {
  campaignRegistrationId: string;
  open: boolean;
  onClose: () => void;
}): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();
  const [selectedBundleId, setSelectedBundleId] = useState<string | undefined>(
    undefined
  );
  const [search, setSearch] = useState("");

  const { data } = useQuery<ListComplianceBundlesQuery>(
    LIST_COMPLIANCE_BUNDLES,
    {
      variables: {
        input: {
          sort: "NAME_ASC",
          filter: { stringContains: search, status: "active" },
          limit: pageSize,
          offset: 0 * pageSize,
        },
      },
    }
  );

  const [reuseBundle, { loading: isSubmitting }] = useMutation<
    ReuseComplianceBundleMutation,
    ReuseComplianceBundleMutationVariables
  >(REUSE_COMPLIANCE_BUNDLE, {
    onErrorSnackbarMessage: "Failed to update registration with bundle",
    refetchQueries: [LIST_COMPLIANCE_BUNDLES, GET_REGISTRATION_FOR_STAFF],
  });

  const handleReuseBundle = useCallback(async () => {
    if (!selectedBundleId) {
      return;
    }

    const result = await reuseBundle({
      variables: {
        input: {
          campaignRegistrationId,
          complianceBundleId: selectedBundleId,
        },
      },
    });

    if (result.errors) {
      enqueueSnackbar("Failed to attach bundle to registration", {
        variant: "error",
      });
    } else {
      enqueueSnackbar("Bundle successfully attached to registration", {
        variant: "success",
      });
      onClose();
    }
  }, [
    selectedBundleId,
    campaignRegistrationId,
    reuseBundle,
    enqueueSnackbar,
    onClose,
  ]);

  const options = useMemo(() => {
    return (
      data?.listComplianceBundles.bundles.map(bundle => ({
        id: bundle.id,
        label: bundle.slug,
      })) || []
    );
  }, [data?.listComplianceBundles]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogContent>
        <SearchableSelect
          onInputChange={value => setSearch(value ?? "")}
          options={options}
          label="Select a bundle"
          onChange={bundleId => setSelectedBundleId(bundleId)}
          value={selectedBundleId}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!selectedBundleId || isSubmitting}
          onClick={handleReuseBundle}
        >
          Apply Bundle to Registration
        </Button>
      </DialogActions>
    </Dialog>
  );
};
